import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const SmtpSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSmtp, setEditingSmtp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    isDefault: false,
    isActive: true
  });

  // Fetch SMTP settings
  const { data: smtpSettings = [], isLoading } = useQuery({
    queryKey: ['smtp-settings'],
    queryFn: async () => {
      const response = await api.get('/smtp-settings');
      return response.data;
    }
  });

  // Create/Update SMTP mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingSmtp) {
        const response = await api.put(`/smtp-settings/${editingSmtp._id}`, data);
        return response.data;
      } else {
        const response = await api.post('/smtp-settings', data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['smtp-settings']);
      setIsModalOpen(false);
      setEditingSmtp(null);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: editingSmtp ? t('smtp.smtpUpdatedSuccess') : t('smtp.smtpCreatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('smtp.smtpSaveFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Delete SMTP mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/smtp-settings/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['smtp-settings']);
      Swal.fire({
        icon: 'success',
        title: t('smtp.deleted'),
        text: t('smtp.smtpDeletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('smtp.smtpDeleteFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Test SMTP mutation
  const testMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/smtp-settings/${id}/test`);
      return response.data;
    },
    onSuccess: (result) => {
      Swal.fire({
        icon: result.success ? 'success' : 'error',
        title: result.success ? t('smtp.connectionSuccessful') : t('smtp.connectionFailed'),
        text: result.message,
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('smtp.testFailed'),
        text: error.response?.data?.message || t('smtp.testConnectionFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/smtp-settings/${id}/set-default`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['smtp-settings']);
      Swal.fire({
        icon: 'success',
        title: t('smtp.defaultSet'),
        text: t('smtp.smtpSetAsDefaultSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      port: 587,
      secure: false,
      username: '',
      password: '',
      fromEmail: '',
      fromName: '',
      isDefault: false,
      isActive: true
    });
  };

  const handleOpenModal = (smtp = null) => {
    if (smtp) {
      setEditingSmtp(smtp);
      setFormData({
        name: smtp.name,
        host: smtp.host,
        port: smtp.port,
        secure: smtp.secure,
        username: smtp.username,
        password: smtp.password,
        fromEmail: smtp.fromEmail,
        fromName: smtp.fromName,
        isDefault: smtp.isDefault,
        isActive: smtp.isActive
      });
    } else {
      setEditingSmtp(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSmtp(null);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('smtp.confirmDelete'),
      text: t('smtp.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('smtp.yesDelete')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const handleTest = (id) => {
    testMutation.mutate(id);
  };

  const handleSetDefault = (id) => {
    setDefaultMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('smtp.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('smtp.subtitle')}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
        >
          {t('smtp.addSmtpAccount')}
        </button>
      </div>

      {/* SMTP Settings List */}
      <div className="card">
        {smtpSettings.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📧</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('smtp.noSmtpSettings')}</h3>
            <p className="text-gray-500 mb-4">{t('smtp.addFirstSmtpAccount')}</p>
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-primary"
            >
              {t('smtp.addSmtpAccount')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('smtp.name')}</th>
                  <th>{t('smtp.host')}</th>
                  <th>{t('smtp.port')}</th>
                  <th>{t('smtp.fromEmail')}</th>
                  <th>{t('smtp.fromName')}</th>
                  <th>{t('smtp.statusSetting')}</th>
                  <th>{t('smtp.default')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {smtpSettings.map((smtp) => (
                  <tr key={smtp._id}>
                    <td className="font-medium">{smtp.name}</td>
                    <td>{smtp.host}</td>
                    <td>{smtp.port}</td>
                    <td>{smtp.fromEmail}</td>
                    <td>{smtp.fromName}</td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        smtp.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {smtp.isActive ? t('smtp.active') : t('smtp.inactive')}
                      </span>
                    </td>
                    <td>
                      {smtp.isDefault ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {t('smtp.default')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(smtp._id)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('smtp.setDefault')}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleTest(smtp._id)}
                          disabled={testMutation.isPending}
                          className="text-green-600 hover:text-green-500 text-sm"
                        >
                          {t('smtp.test')}
                        </button>
                        <button
                          onClick={() => handleOpenModal(smtp)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(smtp._id)}
                          className="text-red-600 hover:text-red-500 text-sm"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SMTP Settings Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingSmtp ? t('smtp.editSmtpSetting') : t('smtp.addSmtpSetting')}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.host')} *
                    </label>
                    <input
                      type="text"
                      name="host"
                      value={formData.host}
                      onChange={handleChange}
                      placeholder="smtp.gmail.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.port')} *
                    </label>
                    <input
                      type="number"
                      name="port"
                      value={formData.port}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="secure"
                        checked={formData.secure}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{t('smtp.useSslTls')}</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.username')} *
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.password')} *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.fromEmail')} *
                    </label>
                    <input
                      type="email"
                      name="fromEmail"
                      value={formData.fromEmail}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('smtp.fromName')} *
                    </label>
                    <input
                      type="text"
                      name="fromName"
                      value={formData.fromName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{t('smtp.setAsDefault')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{t('smtp.active')}</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="btn btn-primary"
                  >
                    {saveMutation.isPending ? t('common.saving') : (editingSmtp ? t('smtp.update') : t('smtp.create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmtpSettings;
